import { Badge, Button, Input, cn } from '@repo/ui'
import { Check, Plus, X } from 'lucide-react'
import { useMemo, useState } from 'react'

export type DocumentSourceItem = { id: number; label: string }

type DocumentSourceSelectorProps = {
  items: Array<DocumentSourceItem>
  selectedIds: Array<number>
  onSelectedIdsChange: (ids: Array<number>) => void
  disabled?: boolean
  heading?: string
  emptyText?: string
  noSelectionText?: string
  searchPlaceholder?: string
  selectionMode?: 'single' | 'multiple'
  selectLabel?: string
  selectedLabel?: string
  showSelectedItems?: boolean
}

export function DocumentSourceSelector({
  items,
  selectedIds,
  onSelectedIdsChange,
  disabled = false,
  heading,
  emptyText = 'No workspace content found.',
  noSelectionText = 'No source content selected.',
  searchPlaceholder = 'Search content',
  selectionMode = 'multiple',
  selectLabel = 'Add',
  selectedLabel = 'Remove',
  showSelectedItems = true
}: DocumentSourceSelectorProps) {
  const [query, setQuery] = useState('')
  const selectedItems = useMemo(
    () =>
      selectedIds
        .map((id) => items.find((item) => item.id === id))
        .filter((item): item is DocumentSourceItem => Boolean(item)),
    [items, selectedIds]
  )
  const filteredItems = useMemo(() => {
    const value = query.trim().toLowerCase()
    return value ? items.filter((item) => item.label.toLowerCase().includes(value)) : items
  }, [items, query])

  function toggle(id: number) {
    if (selectedIds.includes(id)) {
      onSelectedIdsChange(selectedIds.filter((existing) => existing !== id))
    } else {
      onSelectedIdsChange(selectionMode === 'single' ? [id] : [...selectedIds, id])
    }
  }

  return (
    <div className="space-y-2">
      {heading && <h3 className="text-sm font-medium">{heading}</h3>}
      <div className="rounded-md border bg-muted/30 p-3">
        <div className="space-y-3">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            disabled={disabled}
            className="bg-background"
          />
          {showSelectedItems && selectedItems.length > 0 ? (
            <div className="flex flex-wrap gap-2 border-b pb-3">
              {selectedItems.map((item) => (
                <Badge key={item.id} variant="secondary" className="gap-1 pr-1">
                  <span className="max-w-44 truncate">{item.label}</span>
                  <button
                    type="button"
                    aria-label={`Remove selected ${item.label}`}
                    onClick={() => toggle(item.id)}
                    disabled={disabled}
                    className="rounded p-0.5 hover:bg-muted"
                  >
                    <X className="size-3" />
                  </button>
                </Badge>
              ))}
            </div>
          ) : showSelectedItems ? (
            <p className="border-b pb-3 text-sm text-muted-foreground">{noSelectionText}</p>
          ) : null}
          <div className="max-h-48 overflow-y-auto rounded-md border bg-background">
            {items.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">{emptyText}</p>
            ) : filteredItems.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">No matches found.</p>
            ) : (
              filteredItems.map((item) => {
                const selected = selectedIds.includes(item.id)
                const label = selected ? selectedLabel : selectLabel
                return (
                  <div
                    key={item.id}
                    className={cn(
                      'flex items-center justify-between gap-3 border-b p-2 last:border-b-0',
                      selected && 'bg-muted/40'
                    )}
                  >
                    <span className="min-w-0 truncate text-sm">{item.label}</span>
                    <Button
                      type="button"
                      variant={selected ? 'secondary' : 'outline'}
                      size="sm"
                      onClick={() => toggle(item.id)}
                      disabled={disabled}
                      aria-label={`${label} ${item.label}`}
                    >
                      {selected ? <Check className="size-4" /> : <Plus className="size-4" />}
                      {label}
                    </Button>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
